/* ═══════════════════════════════════════════════════════════════════════
   The closed sign.

   letcraig.ai is not taking new businesses while the product is reworked,
   so the root address says so rather than pitching. A front door that
   sells a thing nobody is behind to deliver is worse than a shut one: the
   business that signs up gets nothing, and that is the one impression of
   crAIg they will keep.

   ── This is a holding page, not a deletion ─────────────────────────────
   Landing.tsx is untouched and still imported by main.tsx. Opening again
   is one identifier: render <Landing /> instead of <UnderConstruction />
   in the `!segments[0]` branch of main.tsx, and the pitch is back exactly
   as it was.

   ── What it deliberately does NOT have ─────────────────────────────────
   No sign-up button, no "let crAIg interview you", no email capture. The
   whole reason this page exists is that we are not serving, and a closed
   shop with a working till is just a slower way of letting somebody down.

   Everything else in the app is untouched and still reachable by its own
   address — the dashboard, signing in, and every business's live intake
   at /<slug>. Those pages belong to practices who bought them, and this
   was never a reason to take theirs down.

   No date. We do not know one, and a promise on a holding page is a
   promise somebody will hold us to.
   ═══════════════════════════════════════════════════════════════════════ */

import { useEffect, useState } from "react";
import { applyAccent, applyTheme, prefersDark, type Theme } from "./theme";
import { CraigFace } from "./components/CraigFace";
import { Wordmark } from "./components/Brand";

export function UnderConstruction() {
  /* Read once, from the reader's own setting. There is no toggle: a page
     with three sentences on it does not need a preference, and a control
     that does nothing useful invites a click that achieves nothing. */
  const [theme] = useState<Theme>(() => (prefersDark() ? "dark" : "light"));

  useEffect(() => {
    applyTheme(theme);
    applyAccent(undefined, theme);
  }, [theme]);

  return (
    <div className="min-h-screen grid place-items-center px-5">
      <main className="panel panel-ticks px-6 py-8" style={{ maxWidth: 460, width: "100%" }}>
        <div className="flex items-center gap-2.5">
          <CraigFace size={34} />
          <Wordmark size={17} />
        </div>

        <h1
          className="mono"
          style={{ fontSize: 19, fontWeight: 700, margin: "18px 0 0", lineHeight: 1.3 }}
        >
          We're rebuilding.
        </h1>

        <p style={{ fontSize: 14.5, color: "var(--ink-2)", lineHeight: 1.6, marginTop: 10 }}>
          crAIg is being reworked and isn't taking on new businesses just now.
        </p>
        {/* Said plainly, because the people most likely to read this page
            are the ones already relying on it, and "we're rebuilding" on
            its own reads as "your thing is gone". */}
        <p style={{ fontSize: 14.5, color: "var(--ink-2)", lineHeight: 1.6, marginTop: 10 }}>
          Anything already set up is safe and carries on working.
        </p>
      </main>
    </div>
  );
}
